import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaFileAlt,
    FaFilePdf,
    FaSearch,
    FaPrint,
    FaFileExcel,
    FaCopy
} from 'react-icons/fa';

const AuditRecommendationLetter = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('recommendation-letter-society');
    const departmentStr = isSociety ? 'society' : 'subhe';
    const pageTitle = isSociety ? "मजूर संख्या शिफारस पत्र यादी" : "सु.बे. अभियंता शिफारस पत्र यादी";

    const [registrations, setRegistrations] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);

        // Fetch generated lottery dates from generatework
        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const meetingList = Object.keys(data).map(dateKey => ({
                    id: dateKey,
                    date: dateKey
                }));
                // Sort dates by descending (assuming simple string sort suffices for DD-MMM-YYYY if formatted uniformly, else reverse)
                setMeetings(meetingList.reverse());
            } else {
                setMeetings([]);
            }
        });

        // Fetch Registrations
        const regPath = isSociety ? 'ekamwatapzp/SocietyRegistrations' : 'ekamwatapzp/EngineerRegistrations';
        const regRef = ref(db, regPath);

        onValue(regRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const regs = Object.entries(data).map(([key, val]) => ({
                    ...val,
                    _key: key
                }));
                setRegistrations(regs);
            }
            setLoading(false);
        });

    }, [isSociety]);

    const handleSearch = () => {
        setAppliedDate(selectedDate);
    };

    // Filter and Process Data
    const displayData = useMemo(() => {
        if (!appliedDate) return [];

        const results = [];
        registrations.forEach(reg => {
            if (reg.GeneratedLottery && reg.GeneratedLottery[appliedDate]) {
                const work = reg.GeneratedLottery[appliedDate];
                // Check if this work matches the department (subhe/society)
                const deptMap = { 'subhe': 'सुबे', 'society': 'मजूर' };
                if (!work.sharedwork || work.sharedwork === deptMap[departmentStr]) {
                    results.push({
                        id: work.id || work.workNumber,
                        taluka: work.taluka || reg.taluka || "-",
                        schemeName: work.schemeName || "-",
                        workName: work.workName || "-",
                        tenderAmount: work.tenderAmount || reg.work_payment?.[appliedDate]?.amount || "-",
                        winnerName: reg.firstName ? `${reg.firstName} ${reg.middleName || ''} ${reg.surname}`.trim() : (reg.fullName || reg.societyName || reg.name || "-"),
                        fullReg: reg,
                        workData: work
                    });
                }
            }
        });

        // Apply Search Term
        if (searchTerm) {
            return results.filter(item => 
                item.taluka.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.schemeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.workName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.winnerName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return results;
    }, [registrations, appliedDate, searchTerm, departmentStr]);

    const handleViewLetter = (item) => {
        const winnerData = {
            ...item.fullReg,
            id: item.fullReg._key
        };
        
        navigate('/selection-letter', { 
            state: { 
                workData: {
                    ...item.workData,
                    meetingNumber: appliedDate
                },
                isZpHeadView: true,
                winnerUser: winnerData 
            } 
        });
    };

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#085e53] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#085e53] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">{pageTitle}</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600 font-black">ऑडिट विभाग / {pageTitle}</span>
                </div>
            </div>

            {/* Filter Card */}
            <div className="bg-white rounded mb-6 no-print">
                <div className="p-6">
                    <label className="text-sm text-gray-700 block mb-2 font-semibold">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                        <select 
                            className="w-64 bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#4e73df] transition"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        >
                            <option value="">लॉटरीची तारीख निवडा</option>
                            {meetings.map(m => (
                                <option key={m.id} value={m.id}>{m.date}</option>
                            ))}
                        </select>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#5c6bc0] hover:bg-[#3f51b5] text-white px-6 py-2 rounded shadow-sm text-sm font-semibold transition"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Actions & Search Toggle */}
            <div className="bg-white rounded-t border border-gray-200 p-4 border-b-0 flex flex-col md:flex-row justify-between items-center gap-4 no-print mt-6">
                <div className="flex items-center gap-0">
                    <button className="bg-[#6c757d] border-r border-gray-500 text-white px-4 py-1.5 rounded-l text-sm hover:bg-gray-700 transition">
                        Copy
                    </button>
                    <button className="bg-[#6c757d] border-r border-gray-500 text-white px-4 py-1.5 text-sm hover:bg-gray-700 transition">
                        Excel
                    </button>
                    <button onClick={() => window.print()} className="bg-[#6c757d] text-white px-4 py-1.5 rounded-r text-sm hover:bg-gray-700 transition">
                        Print
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Search:</span>
                    <input 
                        type="text"
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-400 w-48"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-b border border-gray-200 overflow-hidden mb-8">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="border-b border-gray-200">
                            <tr className="text-[13px] font-semibold text-gray-800">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        # <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[120px] whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        तालुका <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[200px] whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        योजनेचे नाव <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[400px]">
                                    <div className="flex items-center justify-between">
                                        कामाचे नाव <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-r border-gray-200 min-w-[200px] whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        {isSociety ? "मजूर संस्थेचे नाव" : "सु.बे. अभियंत्याचे नाव"} <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-center w-20 whitespace-nowrap">
                                    <div className="flex items-center justify-between">
                                        पत्र <FaSort className="text-gray-300 ml-1" />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {displayData.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition-colors text-sm text-gray-700">
                                    <td className="px-4 py-4 border-r border-gray-200">{index + 1}</td>
                                    <td className="px-4 py-4 border-r border-gray-200">{item.taluka}</td>
                                    <td className="px-4 py-4 border-r border-gray-200 leading-relaxed">{item.schemeName}</td>
                                    <td className="px-4 py-4 border-r border-gray-200 leading-relaxed">
                                        <div className="flex flex-col gap-1">
                                            <span>{item.workName}</span>
                                            <span className="text-gray-600 font-medium">Rs- {item.tenderAmount}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 border-r border-gray-200 font-medium">
                                        {item.winnerName}
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <button 
                                            onClick={() => handleViewLetter(item)}
                                            className="text-[#4e73df] hover:text-blue-800 transition p-2 no-print"
                                        >
                                            <FaPrint size={16} />
                                        </button>
                                        <FaPrint size={16} className="hidden print:inline text-[#4e73df] mx-auto" />
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500 bg-gray-50/50">
                                        No data available in table
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Info */}
                <div className="p-4 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-600 no-print">
                    <div>
                        Showing {displayData.length > 0 ? 1 : 0} to {displayData.length} of {displayData.length} entries
                    </div>
                    <div className="flex items-center border border-gray-300 rounded text-sm overflow-hidden">
                        <button className="px-3 py-1.5 border-r border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed">Previous</button>
                        <button className="px-3 py-1.5 border-r border-gray-300 bg-[#5c6bc0] text-white">1</button>
                        <button className="px-3 py-1.5 bg-gray-50 text-[#5c6bc0] hover:bg-gray-100 transition">Next</button>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                    .bg-white { box-shadow: none !important; border: none !important; }
                    table { border: 1px solid #ddd !important; border-collapse: collapse !important; width: 100% !important; }
                    th, td { border: 1px solid #ddd !important; padding: 10px !important; }
                }
            `}</style>
        </div>
    );
};

export default AuditRecommendationLetter;
